import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MessageSquare, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface SavedPrompt {
  id: string;
  prompt_text: string;
  response_text: string | null;
  source: string | null;
  created_at: string;
}

const SavedPrompts: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: savedPrompts, isLoading } = useQuery({
    queryKey: ["saved-prompts"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_saved_prompts")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as SavedPrompt[];
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("user_saved_prompts")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["saved-prompts"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Prompt removed");
    },
    onError: () => {
      toast.error("Failed to remove prompt");
    },
  });

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
          <MessageSquare className="h-7 w-7 text-primary" />
          Chat History
        </h1>
        <p className="text-muted-foreground mt-1">
          AI conversations and responses you've saved.
        </p>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground">Loading...</div>
      ) : !savedPrompts?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">No saved prompts yet.</p>
            <p className="text-sm text-muted-foreground mt-2">
              Save prompts from the AI chat by clicking the bookmark icon.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {savedPrompts.map((prompt) => (
            <Card key={prompt.id} className="group">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <CardTitle className="text-base font-medium text-foreground">
                      {prompt.prompt_text.substring(0, 100)}
                      {prompt.prompt_text.length > 100 && "..."}
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">
                      {format(new Date(prompt.created_at), "PPp")} • {prompt.source || "chat"}
                    </p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyToClipboard(prompt.prompt_text)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => removeMutation.mutate(prompt.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {prompt.response_text && (
                <CardContent>
                  <div className="bg-muted/50 rounded-md p-3 text-sm text-muted-foreground">
                    {prompt.response_text.substring(0, 300)}
                    {prompt.response_text.length > 300 && "..."}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SavedPrompts;
